import { alpha, createTheme } from "@mui/material";
import { grey } from "@mui/material/colors";
import { TypographyOptions } from "@mui/material/styles/createTypography";

declare module "@mui/material/Typography" {
  interface TypographyPropsVariantOverrides {
    title1: true;
    title2: true;
    title3: true;
    lgBody1: true;
    lgBody2: true;
    body3: true;
    body4: true;
    body5: true;
    body6: true;
    link: true;
    label: true;
    dropdownPlaceholder: true;
  }
}
interface ExtendedTypographyOptions extends TypographyOptions {
  title1: React.CSSProperties;
  title2: React.CSSProperties;
  title3: React.CSSProperties;
  lgBody1: React.CSSProperties;
  lgBody2: React.CSSProperties;
  body3: React.CSSProperties;
  body4: React.CSSProperties;
  body5: React.CSSProperties;
  body6: React.CSSProperties;
  link: React.CSSProperties;
  label: React.CSSProperties;
  dropdownPlaceholder: React.CSSProperties;
}

// Typescript module augmentation
declare module "@mui/material/styles" {
  interface BreakpointOverrides {
    // xxs: true;
    xs: true;
    sm: true;
    md: true;
    lg: true;
    lg1: true;
    xl: true;
    xl1: true;
    xxl: true;
  }
}
export const fontFamily = "Inter !important";

const palette = {
  common: {
    white: "#FFFFFF",
    black: "#000000 ",
  },
  primary: {
    main: "#0682B5",
    light: "#EEFAFF",
    dark: "#002D3F",
  },
  secondary: {
    main: "#00BAC7",
    light: "#F2F6F8",
    dark: "#21262B",
  },
  grey: {
    A100: "#565656", //table color
    A200: "#F4F4F4", //table head background
    A400: "#D2D2D2", // textField border
    A600: "#9B9D9F", //textField Input
    A700: "#F2F6F8", //textField Input
  },
  text: {
    primary: "#344054", //input label
    secondary: "#909195", //placeholder
    disabled: "#E9E9E9", //border
  },
  error: {
    main: "#D92D20", // error button / text
    dark: "#B42318", // error button / text - hovered
    light: "#F04438", // error border
    contrastText: "#FECDCA", // In-active background
  },
  background: {
    default: alpha("#F0F3F6", 1),
    disabled: "#DBDBDB",
  },
  success: {
    main: "#087B00",
  },
};

const drawerHandlers = new WeakMap<HTMLElement, (e: KeyboardEvent) => void>();
const drawerFocusCleanups = new WeakMap<HTMLElement, () => void>();
const dialogHandlers = new WeakMap<HTMLElement, (e: KeyboardEvent) => void>();
const dialogFocusCleanups = new WeakMap<HTMLElement, () => void>();

const FOCUS_QUERY =
  'input:not([type="hidden"]):not([type="button"]):not([type="submit"]):not([type="file"]):not([disabled]):not([readonly]), textarea:not([disabled]):not([readonly]), select:not([disabled]), .MuiSelect-select:not([aria-disabled="true"])';

function setupAutoFocus(
  node: HTMLElement,
  focusCleanups: WeakMap<HTMLElement, () => void>,
) {
  const firstField = node.querySelector<HTMLElement>(FOCUS_QUERY);
  if (firstField) {
    firstField.focus();
    return;
  }
  let timer: ReturnType<typeof setTimeout>;
  const observer = new MutationObserver(() => {
    const field = node.querySelector<HTMLElement>(FOCUS_QUERY);
    if (field) {
      field.focus();
      cleanup();
    }
  });
  const cleanup = () => {
    observer.disconnect();
    clearTimeout(timer);
    focusCleanups.delete(node);
  };
  observer.observe(node, { childList: true, subtree: true });
  timer = setTimeout(cleanup, 5000);
  focusCleanups.set(node, cleanup);
}

export const theme = createTheme({
  breakpoints: {
    values: {
      xs: 0, // Extra small devices (portrait phones)
      sm: 600, // Small devices (landscape phones)
      md: 960, // Medium devices (tablets)
      lg: 1280, // Large devices (desktops)
      lg1: 1366, // Large devices (desktops)
      xl: 1440, // Extra large devices (large desktops)
      xl1: 1536, // Large devices (desktops)
      xxl: 1920, // Extra extra large devices (large desktops)
    },
  },
  zIndex: {
    drawer: 2, //used for drawer
  },
  palette: {
    primary: {
      main: palette.primary.main,
      light: palette.primary.light,
      dark: palette.primary.dark,
    },
    secondary: {
      main: palette.secondary.main,
      light: palette.secondary.light,
      dark: palette.secondary.dark,
    },
    background: {
      default: palette.background.default,
    },
    grey: palette.grey,
  },
  typography: {
    fontFamily,
    lgBody1: {
      fontSize: "24px",
      letterSpacing: "1%",
    },
    lgBody2: {
      fontSize: "18px",
      letterSpacing: "0.8%",
    },
    title1: {
      fontSize: "18px",
      letterSpacing: "0.25%",
    },
    title2: {
      fontSize: "16px",
      letterSpacing: "0.15%",
    },
    title3: {
      fontSize: "14px",
      letterSpacing: "1%",
    },
    body2: {
      fontSize: "14px",
      letterSpacing: "0.2%",
    },
    body3: {
      fontSize: "12px",
      letterSpacing: "0.2%",
    },
    body4: {
      fontSize: "14px",
      letterSpacing: "0.4%",
    },
    label: {
      color: grey[600],
      fontSize: "14px",
      fontStyle: "normal",
      lineHeight: "130%",
      letterSpacing: "0.12px",
    },
    dropdownPlaceholder: {
      fontSize: "14px",
      fontStyle: "normal",
      fontWeight: 500,
      lineHeight: "150%",
      letterSpacing: "0.1px",
      color: "#b3b3b3",
    },
  } as ExtendedTypographyOptions,
  components: {
    MuiTypography: {
      styleOverrides: {
        h1: {
          fontSize: "48px !important",
        },
        h2: {
          fontSize: "40px !important",
        },
        h3: {
          fontSize: "32px !important",
        },
        h4: {
          fontSize: "24px !important",
        },
        h5: {
          fontSize: "20px !important",
        },
        h6: {
          fontSize: "16px !important",
        },
        subtitle1: {
          fontSize: 18,
        },
        subtitle2: {
          fontSize: 16,
        },
        caption: {
          fontSize: 14,
        },
        body1: {
          fontSize: 24,
        },
        body2: {
          fontSize: 12,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          "&.MuiButton-containedPrimary": {
            borderRadius: "8px",
            boxShadow: "none",
            // "&:hover": {
            //   backgroundColor: (theme) => theme.palette.primary.light,
            // },
          },
          "&.MuiButton-outlined": {
            borderRadius: "8px",
            boxShadow: "none",
            // "&:hover": {
            //   backgroundColor: (theme) =>
            //     alpha(theme.palette.primary.light, 0.3),
            // },
          },
        },
      },
    },
    MuiPaginationItem: {
      styleOverrides: {
        root: {
          backgroundColor: "#fff",
          "&.Mui-selected": {
            backgroundColor: palette.primary.main,
            color: "white",
            "&:hover": {
              backgroundColor: palette.primary.light,
            },
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          "&.MuiMenu-paper": {
            boxShadow: "none",
            border: ".8px solid lightgrey",
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          border: "none",
          // "&.MuiAppBar-colorDefault": {
          //   backgroundColor: alpha("#486CB1", 0.001)
          // }
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        input: {
          "&::placeholder": {
            fontSize: "14px", // Set your desired font size for placeholder text
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        select: {
          "&::placeholder": {
            fontSize: "14px", // Set your desired font size for select placeholder text
            color: "#393939",
          },
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          marginLeft: 0,
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: "8px",
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderWidth: "2px",
            borderColor: "#0682B5",
          },
        },
      },
    },
    MuiButtonBase: {
      styleOverrides: {
        root: {
          "&.Mui-focusVisible": {
            outline: "2px solid #0682B5",
            outlineOffset: "2px",
            borderRadius: "4px",
          },
        },
      },
    },
    MuiDrawer: {
      defaultProps: {
        SlideProps: {
          appear: true,
          onEntered: (node: HTMLElement) => {
            setupAutoFocus(node, drawerFocusCleanups);
            const handler = (e: KeyboardEvent) => {
              if (e.key !== "Enter" || e.shiftKey) return;
              const active = document.activeElement as HTMLElement;
              if (
                ["TEXTAREA", "INPUT", "SELECT", "BUTTON"].includes(
                  active?.tagName,
                )
              )
                return;
              if (document.querySelector('[role="listbox"],[role="menu"]'))
                return;
              node.querySelector<HTMLFormElement>("form")?.requestSubmit();
            };
            drawerHandlers.set(node, handler);
            node.addEventListener("keydown", handler);
          },
          onExited: (node: HTMLElement) => {
            drawerFocusCleanups.get(node)?.();
            const handler = drawerHandlers.get(node);
            if (handler) {
              node.removeEventListener("keydown", handler);
              drawerHandlers.delete(node);
            }
          },
        },
      },
    },
    MuiDialog: {
      defaultProps: {
        TransitionProps: {
          onEntered: (node: HTMLElement) => {
            setupAutoFocus(node, dialogFocusCleanups);
            const handler = (e: KeyboardEvent) => {
              if (e.key !== "Enter" || e.shiftKey) return;
              const active = document.activeElement as HTMLElement;
              if (
                ["TEXTAREA", "INPUT", "SELECT", "BUTTON"].includes(
                  active?.tagName,
                )
              )
                return;
              if (document.querySelector('[role="listbox"],[role="menu"]'))
                return;
              node.querySelector<HTMLFormElement>("form")?.requestSubmit();
            };
            dialogHandlers.set(node, handler);
            node.addEventListener("keydown", handler);
          },
          onExited: (node: HTMLElement) => {
            dialogFocusCleanups.get(node)?.();
            const handler = dialogHandlers.get(node);
            if (handler) {
              node.removeEventListener("keydown", handler);
              dialogHandlers.delete(node);
            }
          },
        },
      },
    },
  },
});
